// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { USA_ABBR } from "@steiner/XDL/constants";
import xPatterns, { commitMask } from "@steiner/XDL/Utilities/xValidate/xPatterns";
import * as Yup from "yup";

type xMasksType = {
    [key: string]: { transform: (newValue: string, oldValue: string, ...options: any) => string; validate?: string };
};

/**
 * define your mask or transformation here
 */
const xMasks: xMasksType = {
    /**
     * phone mask
     */
    phone: {
        /**
         * transform
         * @param {string} newValue
         * @param {string} oldValue
         * @param {...any} params
         * @returns manipulated string
         */
        transform: (newValue, oldValue) => commitMask(newValue, oldValue, xPatterns.phone),

        /**
         * this is a default validation for the mask, can be override if user enter additional validators
         */
        validate: "phone",
    },

    /**
     * max transformation
     */
    max: {
        transform: (newValue, oldValue, max) => String(newValue).slice(0, max),
    },

    /**
     * uppercase transformation
     */
    uppercase: {
        transform: (newValue, oldValue) => String(newValue).toUpperCase(),
    },

    /**
     * lowercase transformation
     */
    lowercase: {
        transform: (newValue, oldValue) => String(newValue).toLowerCase(),
    },

    limit: {
        transform: (newValue, oldValue, max) => String(newValue).slice(0, max),
    },
};

/**
 * Define Custom Validator and Mask noEmptySpace
 * @usage noEmptySpace:force
 */
Yup.addMethod<Yup.StringSchema>(Yup.string, "noEmptySpace", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-no-empty-space`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        return (
            /^\S+$/.test(value) ||
            createError({
                path,
                message: errorMessage || "we shouldn't have any white space in this field",
            })
        );
    });
});

// define the mask for noEmptySpace:force
xMasks.noEmptySpace = {
    transform: (newValue, oldValue, options) =>
        options === "force" ? String(newValue).replace(/\s+/gm, "") : newValue,
};

/**
 * Postal Code ( ZIP Code) Validation
 */
Yup.addMethod<Yup.StringSchema>(Yup.string, "postalCode", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-no-empty-space`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        // any additional country regex can be found here
        // https://www.codegrepper.com/code-examples/whatever/validate+postal+code+in+yup
        const regex: { [key: string]: RegExp } = {
            US: /\d{5}([ \-]\d{4})?/gm,
            CA: /[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d/gm,
        };

        const country = options ?? USA_ABBR;
        if (!regex[country]) {
            throw new Error(`Country ${country} is not supported validation`);
        }

        return (
            (regex[country] && regex[country].test(value)) ||
            createError({
                path,
                message: errorMessage || "Invalid Postal Code",
            })
        );
    });
});

// postalCode Mask
xMasks.postalCode = {
    transform: (newValue, oldValue, options) => {
        const country = options ?? USA_ABBR;
        if (country === USA_ABBR) {
            return newValue.length > 5
                ? commitMask(newValue, oldValue, xPatterns.longZipCode)
                : commitMask(newValue, oldValue, xPatterns.shortZipCode);
        }
        return newValue;
    },
    validate: "min:5|max:10",
};

// Email Validation
Yup.addMethod<Yup.StringSchema>(Yup.string, "email", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-email-address`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        if (!value) {
            return true;
        }

        return (
            /^\w+([-.]\w+)*@\w+([-.]\w+)*\.\w{2,3}$/.test(value) ||
            createError({
                path,
                message: errorMessage || "Invalid Email Address",
            })
        );
    });
});

// Example on confirm on IBT - BasicInfoForm.tsx
Yup.addMethod<Yup.StringSchema>(Yup.string, "confirm", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-confirm`, errorMessage, function (this: any, value: string) {
        const { path, createError, parent } = this;
        const [fieldToEqual, fieldLabel] = options.split("+");

        return (
            parent[fieldToEqual] === value ||
            createError({ path, message: translate("this field should be equal to {0}", fieldLabel) })
        );
    });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, "phone", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-phone-number`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        if (!value) {
            return true;
        }

        return (
            /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/.test(value) ||
            createError({
                path,
                message: errorMessage || "Invalid format.  Please format as: (123) 456-7890",
            })
        );
    });
});

// URL Validation
Yup.addMethod<Yup.StringSchema>(Yup.string, "url", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-url-address`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        if (!value) {
            return true;
        }
        return (
            /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/.test(
                value,
            ) ||
            createError({
                path,
                message: errorMessage || "Invalid URL",
            })
        );
    });
});

// used for checking orderHistoryAmount filter, it doesnt allow letters, characters other thn digits,"-" , "."
// Yup validation method for amount (No negative numbers allowed)
Yup.addMethod<Yup.StringSchema>(Yup.string, "amount", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-amount`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;
        return (
            /^\d+(\.\d{1,2})?$/.test(value) || // No "-" allowed, only up to 2 decimals
            createError({
                path,
                message: errorMessage || "Invalid Characters",
            })
        );
    });
});

// XNG-Change: this function allows only numeric characters with up to 2 decimal places
function checkAmount(nStr: string) {
    // Remove invalid characters (digits and dot only, no "-")
    nStr = nStr.replace(/[^0-9.]/g, "");

    // Ensure only one decimal point
    const parts = nStr.split(".");
    if (parts.length > 2) {
        nStr = `${parts[0]}.${parts.slice(1).join("")}`;
    }

    // Limit to 2 decimal places
    if (parts.length === 2) {
        nStr = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    return nStr;
}

xMasks.amount = {
    transform: (newValue, oldValue) => checkAmount(newValue),
};

export default xMasks;
